import React, { useMemo, useState } from 'react';
import { avatarColor, avatarInitials, displayName } from './avatarUtils';

function formatMinutes(minutes = 0) {
  const safeMinutes = Math.max(0, Math.round(Number(minutes) || 0));
  const hours = Math.floor(safeMinutes / 60);
  const remainder = safeMinutes % 60;
  if (!hours) return `${remainder}m`;
  if (!remainder) return `${hours}h`;
  return `${hours}h ${String(remainder).padStart(2, '0')}m`;
}

function startOfWeek(offset = 0) {
  const now = new Date();
  const monday = new Date(now);
  monday.setHours(0, 0, 0, 0);
  monday.setDate(now.getDate() - ((now.getDay() + 6) % 7) + offset * 7);
  return monday;
}

function weekDays(offset = 0) {
  const monday = startOfWeek(offset);
  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(monday);
    date.setDate(monday.getDate() + index);
    return date;
  });
}

function sameDay(left, right) {
  return left.getFullYear() === right.getFullYear()
    && left.getMonth() === right.getMonth()
    && left.getDate() === right.getDate();
}

function priorityTone(priorityKey) {
  if (priorityKey === 'critica' || priorityKey === 'alta') return 'bg-rose-500 text-white';
  if (priorityKey === 'media') return 'bg-amber-400 text-slate-950';
  if (priorityKey === 'baixa') return 'bg-emerald-500 text-white';
  return 'bg-slate-300 text-slate-700';
}

function overloadTone(minutes) {
  if (minutes >= 480) return 'border-rose-200 bg-rose-50 text-rose-700';
  if (minutes >= 300) return 'border-amber-200 bg-amber-50 text-amber-700';
  return 'border-emerald-200 bg-emerald-50 text-emerald-700';
}

function weekButtonTone(isActive) {
  return isActive
    ? 'border-blue-200 bg-blue-50 font-medium text-blue-700 hover:bg-blue-100'
    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50';
}

function normalizeLegacyItems(items) {
  return (items || []).map(item => {
    const values = item.column_values || {};
    const people = Array.isArray(values.persons) && values.persons.length
      ? values.persons.map((person, index) =>
          typeof person === 'string'
            ? { id: `${person}-${index}`, name: person }
            : { id: person.id || person.user_id || person.email || `${person.name}-${index}`, name: person.name || person.email || 'Usuário' }
        )
      : (values.person ? [{ id: values.person_id || values.person, name: values.person }] : []);

    return {
      id: item.id,
      name: item.name,
      due_date: values.due_date || null,
      assignees: people,
      priority_key: values.priority || 'sem_prioridade',
      planned_minutes: 0,
      tracked_minutes: 0,
      is_done: ['feito', 'done', 'concluido', 'concluído'].includes(String(values.status || '').toLowerCase()),
      is_overdue: false,
      group_title: '',
      parent_name: null,
      item_type: 'task',
    };
  });
}

export default function ProjectWorkload({ board, items, members, insights }) {
  const [weekOffset, setWeekOffset] = useState(0);

  const sourceItems = useMemo(
    () => (insights?.workload_items?.length ? insights.workload_items : normalizeLegacyItems(items)),
    [insights, items]
  );

  const days = useMemo(() => weekDays(weekOffset), [weekOffset]);

  const memberMap = useMemo(() => {
    const map = new Map();
    (members || []).forEach(member => {
      const entry = {
        id: member.user_id || member.id,
        name: member.display_label || member.user_name || member.name || member.user_email || member.email || 'Usuário',
      };
      if (entry.id) map.set(entry.id, entry);
    });
    sourceItems.forEach(item => {
      (item.assignees || []).forEach(assignee => {
        if (assignee?.id && !map.has(assignee.id)) {
          map.set(assignee.id, { id: assignee.id, name: assignee.name || 'Usuário' });
        }
      });
    });
    return map;
  }, [members, sourceItems]);

  const memberList = useMemo(() => Array.from(memberMap.values()), [memberMap]);

  const itemsForMemberAndDay = (memberId, day) => sourceItems.filter(item => {
    if (!item.due_date) return false;
    const due = new Date(`${String(item.due_date).slice(0, 10)}T12:00:00`);
    if (!sameDay(due, day)) return false;
    return (item.assignees || []).some(assignee => assignee.id === memberId);
  });

  const itemsWithoutAssigneeByDay = day => sourceItems.filter(item => {
    if (!item.due_date) return false;
    const due = new Date(`${String(item.due_date).slice(0, 10)}T12:00:00`);
    return sameDay(due, day) && !(item.assignees || []).length;
  });

  const allocatedMinutes = item => Math.round((item.planned_minutes || 0) / Math.max((item.assignees || []).length, 1));
  const allocatedTrackedMinutes = item => Math.round((item.tracked_minutes || 0) / Math.max((item.assignees || []).length, 1));

  const summary = useMemo(() => ({
    planned: sourceItems.reduce((total, item) => total + (item.planned_minutes || 0), 0),
    tracked: sourceItems.reduce((total, item) => total + (item.tracked_minutes || 0), 0),
    overdue: sourceItems.filter(item => item.is_overdue).length,
    unassigned: sourceItems.filter(item => !(item.assignees || []).length).length,
  }), [sourceItems]);

  return (
    <section className="min-h-[calc(100vh-190px)] px-3 py-4 sm:px-5 lg:px-7">
      <div className="mx-auto w-full max-w-none space-y-5">
        <header className="flex flex-col gap-4 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-slate-900">{board?.name || 'Quadro'}</h2>
            <p className="mt-1 text-sm text-slate-500">
              Carga distribuída por esforço planejado, com subtarefas e rateio entre responsáveis.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setWeekOffset(value => value - 1)}
              className={`rounded-xl border px-3 py-2 text-sm ${weekButtonTone(weekOffset < 0)}`}
            >
              Semana anterior
            </button>
            <button
              type="button"
              onClick={() => setWeekOffset(0)}
              className={`rounded-xl border px-3 py-2 text-sm ${weekButtonTone(weekOffset === 0)}`}
            >
              Semana atual
            </button>
            <button
              type="button"
              onClick={() => setWeekOffset(value => value + 1)}
              className={`rounded-xl border px-3 py-2 text-sm ${weekButtonTone(weekOffset > 0)}`}
            >
              Próxima semana
            </button>
          </div>
        </header>

        <div className="grid gap-4 lg:grid-cols-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Planejado</p>
            <p className="mt-2 text-2xl font-bold text-slate-900">{formatMinutes(summary.planned)}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Rastreado</p>
            <p className="mt-2 text-2xl font-bold text-emerald-700">{formatMinutes(summary.tracked)}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Atrasados</p>
            <p className="mt-2 text-2xl font-bold text-amber-700">{summary.overdue}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Sem responsável</p>
            <p className="mt-2 text-2xl font-bold text-rose-700">{summary.unassigned}</p>
          </div>
        </div>

        <div className="overflow-x-auto rounded-3xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full min-w-[1180px]">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="w-56 px-4 py-4 text-left text-sm font-semibold text-slate-700">Responsável</th>
                {days.map(day => {
                  const totalMinutes = sourceItems.reduce((total, item) => {
                    if (!item.due_date) return total;
                    const due = new Date(`${String(item.due_date).slice(0, 10)}T12:00:00`);
                    if (!sameDay(due, day)) return total;
                    return total + (item.planned_minutes || 0);
                  }, 0);
                  return (
                    <th key={day.toISOString()} className="min-w-[132px] border-l border-slate-200 px-3 py-4 text-center">
                      <div className="text-sm font-semibold text-slate-700">
                        {day.toLocaleDateString('pt-BR', { weekday: 'short' })}
                      </div>
                      <div className="text-xs text-slate-500">
                        {day.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                      </div>
                      <div className={`mt-2 inline-flex rounded-full border px-2 py-1 text-[11px] font-semibold ${overloadTone(totalMinutes)}`}>
                        {formatMinutes(totalMinutes)}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {!memberList.length && (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-sm text-slate-500">
                    Nenhum responsável encontrado para a semana selecionada.
                  </td>
                </tr>
              )}

              {memberList.map(member => {
                const memberItems = sourceItems.filter(item => (item.assignees || []).some(assignee => assignee.id === member.id));
                const memberPlanned = memberItems.reduce((total, item) => total + allocatedMinutes(item), 0);

                return (
                  <tr key={member.id} className="border-b border-slate-100 align-top hover:bg-slate-50/60">
                    <td className="border-r border-slate-200 px-4 py-4">
                      <div className="flex items-center gap-3">
                        <span className={`flex h-10 w-10 items-center justify-center rounded-full ${avatarColor(member.name, member.id)} text-sm font-bold text-white`}>
                          {avatarInitials(member.name, member.id)}
                        </span>
                        <div className="min-w-0">
                          <p className="truncate font-semibold text-slate-900">{member.name}</p>
                          <p className="text-xs text-slate-500">
                            {memberItems.length} itens · {formatMinutes(memberPlanned)} alocados
                          </p>
                        </div>
                      </div>
                    </td>

                    {days.map(day => {
                      const dayItems = itemsForMemberAndDay(member.id, day);
                      const dayPlanned = dayItems.reduce((total, item) => total + allocatedMinutes(item), 0);
                      const dayTracked = dayItems.reduce((total, item) => total + allocatedTrackedMinutes(item), 0);

                      return (
                        <td key={`${member.id}-${day.toISOString()}`} className="border-l border-slate-100 px-3 py-3">
                          <div className="space-y-2">
                            <div className={`inline-flex rounded-full border px-2 py-1 text-[11px] font-semibold ${overloadTone(dayPlanned)}`}>
                              {formatMinutes(dayPlanned)}
                            </div>
                            {dayTracked > 0 && (
                              <div className="text-[11px] font-medium text-emerald-700">
                                {formatMinutes(dayTracked)} rastreados
                              </div>
                            )}
                            {dayItems.map(item => (
                              <article key={`${member.id}-${item.id}`} className="rounded-2xl border border-slate-200 bg-white p-2.5 shadow-sm">
                                <div className="flex items-start justify-between gap-2">
                                  <p className="line-clamp-2 text-xs font-semibold text-slate-800">{item.name}</p>
                                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${priorityTone(item.priority_key)}`}>
                                    {item.priority_label || item.priority_key || 'prioridade'}
                                  </span>
                                </div>
                                <div className="mt-2 text-[11px] text-slate-500">
                                  <p>{item.parent_name ? `Subtarefa de ${item.parent_name}` : item.group_title || 'Tarefa principal'}</p>
                                  <p className="mt-1 font-medium text-slate-700">
                                    {formatMinutes(allocatedMinutes(item))} planejados
                                  </p>
                                </div>
                              </article>
                            ))}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}

              {days.some(day => itemsWithoutAssigneeByDay(day).length > 0) && (
                <tr className="align-top bg-amber-50/40">
                  <td className="border-r border-slate-200 px-4 py-4">
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-200 text-sm font-bold text-slate-500">?</span>
                      <div>
                        <p className="font-semibold text-slate-700">Sem responsável</p>
                        <p className="text-xs text-slate-500">Itens que ainda precisam ser distribuídos</p>
                      </div>
                    </div>
                  </td>
                  {days.map(day => {
                    const dayItems = itemsWithoutAssigneeByDay(day);
                    const dayPlanned = dayItems.reduce((total, item) => total + (item.planned_minutes || 0), 0);
                    return (
                      <td key={`unassigned-${day.toISOString()}`} className="border-l border-slate-100 px-3 py-3">
                        <div className="space-y-2">
                          <div className={`inline-flex rounded-full border px-2 py-1 text-[11px] font-semibold ${overloadTone(dayPlanned)}`}>
                            {formatMinutes(dayPlanned)}
                          </div>
                          {dayItems.map(item => (
                            <article key={item.id} className="rounded-2xl border border-amber-200 bg-white p-2.5 shadow-sm">
                              <p className="line-clamp-2 text-xs font-semibold text-slate-800">{item.name}</p>
                              <p className="mt-2 text-[11px] text-slate-500">
                                {item.parent_name ? `Subtarefa de ${item.parent_name}` : item.group_title || 'Sem grupo visível'}
                              </p>
                            </article>
                          ))}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
